/**
 * ChatbotFAQs — 챗봇 FAQ CRUD
 *
 * 시스템 프롬프트의 FAQ 섹션에 임베드되는 항목들을 관리.
 * 변경 시 서버 in-memory 캐시가 무효화되어 다음 챗봇 호출부터 즉시 반영된다.
 */

import { useCallback, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { useApiFetch } from '../hooks/useApiFetch'
import { apiFetch } from '../lib/api'
import { useToast } from '../components/ToastContainer'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Toggle } from '../ui/Toggle'
import { AdminTable } from '../ui/AdminTable'
import type { TableColumn } from '../ui/Table'

interface ChatbotFaq {
  id: string
  category: string
  question: string
  answer: string
  sortOrder: number
  isActive: boolean
  updatedBy: string | null
  createdAt: string
  updatedAt: string
}

interface FaqsResponse {
  items: ChatbotFaq[]
}

interface DraftFaq {
  id?: string
  category: string
  question: string
  answer: string
  sortOrder: number
  isActive: boolean
}

const EMPTY_DRAFT: DraftFaq = {
  category: '결제',
  question: '',
  answer: '',
  sortOrder: 0,
  isActive: true,
}

export function ChatbotFAQs() {
  const { data, isLoading, refetch } = useApiFetch<FaqsResponse>('/api/admin/chatbot-faqs')
  const { addToast } = useToast()

  const [editing, setEditing] = useState<DraftFaq | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const items = data?.items ?? []
  const categories = useMemo(() => {
    const set = new Set<string>()
    for (const it of items) set.add(it.category)
    return Array.from(set).sort()
  }, [items])

  const openCreate = () => setEditing({ ...EMPTY_DRAFT })
  const openEdit = (faq: ChatbotFaq) =>
    setEditing({
      id: faq.id,
      category: faq.category,
      question: faq.question,
      answer: faq.answer,
      sortOrder: faq.sortOrder,
      isActive: faq.isActive,
    })

  const handleSave = useCallback(async () => {
    if (!editing) return
    if (!editing.category.trim() || !editing.question.trim() || !editing.answer.trim()) {
      addToast({ type: 'error', message: '카테고리/질문/답변은 필수입니다' })
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        category: editing.category.trim(),
        question: editing.question.trim(),
        answer: editing.answer.trim(),
        sortOrder: editing.sortOrder,
        isActive: editing.isActive,
      }
      if (editing.id) {
        await apiFetch(`/api/admin/chatbot-faqs/${editing.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        })
        addToast({ type: 'success', message: 'FAQ가 수정되었습니다' })
      } else {
        await apiFetch('/api/admin/chatbot-faqs', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        addToast({ type: 'success', message: 'FAQ가 추가되었습니다' })
      }
      setEditing(null)
      refetch()
    } catch (err) {
      addToast({ type: 'error', message: (err as Error).message })
    } finally {
      setSubmitting(false)
    }
  }, [editing, refetch, addToast])

  const columns: TableColumn<ChatbotFaq>[] = [
    {
      key: 'category',
      label: '카테고리',
      width: '120px',
      render: (row) => <Badge>{row.category}</Badge>,
    },
    { key: 'question', label: '질문', render: (row) => <strong>{row.question}</strong> },
    {
      key: 'answer',
      label: '답변',
      render: (row) => (
        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {row.answer}
        </span>
      ),
    },
    { key: 'sortOrder', label: '순서', width: '70px', align: 'center', render: (row) => row.sortOrder },
    {
      key: 'isActive',
      label: '활성',
      width: '70px',
      align: 'center',
      render: (row) => (row.isActive ? <Badge color="green">ON</Badge> : <Badge color="red">OFF</Badge>),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>챗봇 FAQ</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--ds-color-text-secondary)', fontSize: 13 }}>
            저장 즉시 챗봇 시스템 프롬프트에 반영됩니다 (서버 캐시 60초 이내).
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus size={16} /> 새 FAQ
        </Button>
      </div>

      <AdminTable
        data={items}
        columns={columns}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="등록된 FAQ가 없습니다"
        actions={[{ label: '수정', variant: 'ghost', onClick: openEdit }]}
        delete={{
          message: (row) => `"${row.question}" 항목을 삭제하시겠어요?`,
          onDelete: async (row) => {
            await apiFetch(`/api/admin/chatbot-faqs/${row.id}`, { method: 'DELETE' })
            refetch()
          },
          successMessage: 'FAQ가 삭제되었습니다',
        }}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'FAQ 수정' : '새 FAQ'}>
        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 480 }}>
            <label>
              <div style={{ fontSize: 13, marginBottom: 4 }}>카테고리</div>
              <Input
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                placeholder="결제, 크레딧, 청첩장 등"
                list="chatbot-faq-categories"
              />
              <datalist id="chatbot-faq-categories">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>

            <label>
              <div style={{ fontSize: 13, marginBottom: 4 }}>질문</div>
              <Input
                value={editing.question}
                onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                placeholder="환불 정책이 어떻게 되나요?"
              />
            </label>

            <label>
              <div style={{ fontSize: 13, marginBottom: 4 }}>답변</div>
              <textarea
                style={{
                  width: '100%',
                  minHeight: 120,
                  padding: '8px 12px',
                  border: '1px solid var(--ds-color-border)',
                  borderRadius: 'var(--ds-radius-md)',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  lineHeight: 1.5,
                  resize: 'vertical',
                }}
                value={editing.answer}
                onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                placeholder="결제일로부터 7일 이내, 미발행 청첩장에 한해 환불 가능합니다..."
              />
            </label>

            <div style={{ display: 'flex', gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div style={{ fontSize: 13, marginBottom: 4 }}>정렬 순서</div>
                <Input
                  type="number"
                  value={editing.sortOrder}
                  onChange={(e) =>
                    setEditing({ ...editing, sortOrder: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </label>
              <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 13 }}>활성</div>
                <Toggle
                  checked={editing.isActive}
                  onChange={(checked) => setEditing({ ...editing, isActive: checked })}
                />
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={submitting}>
                취소
              </Button>
              <Button onClick={handleSave} loading={submitting}>
                저장
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
